// Who may read BI, and who may spend on it.
//
// TWO SERVER RULES ARE MIRRORED HERE, and neither is enforced here. The refusal
// is the permission; the console declining to draw a control is manners:
//   * every route needs `bi_site` at `full`, the READ included, so a `read`
//     holder gets a 403 on load. That is the ordinary answer for most of the
//     company, so it is rendered as an explanation rather than an error.
//   * nobody may edit their own row, so escalation takes two people and
//     leaves both in the log.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Input, useToast } from "@bi/shared";

import { ErrorState, LoadingState, errorMessage } from "../components/states";
import { ApiError, revokeBiGrant, setBiGrant } from "../lib/api";
import { isSelfGrant, sortGrants } from "../lib/grant-rules";
import { biGrantsQuery, permissionKeys, taggingIdentityQuery } from "../lib/queries";

/** What each level actually buys, said as the consequence rather than the
 *  mechanism, because that is what an admin is choosing between. */
const LEVEL_BLURB: Record<string, string> = {
  read: "See every dashboard. Cannot edit a tagger or start a run.",
  full: "Everything read has, plus editing taggers, starting runs that spend, and this page.",
};

function formatDay(iso: string): string {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "-" : new Date(t).toLocaleDateString();
}

export function PermissionsPage() {
  const grants = useQuery(biGrantsQuery);
  const me = useQuery(taggingIdentityQuery);
  const qc = useQueryClient();
  const toast = useToast();

  const [email, setEmail] = useState("");
  const [access, setAccess] = useState<"read" | "full">("read");
  // Which row has asked to be revoked but not yet confirmed. A two-step click
  // rather than a dialog: `@bi/shared`'s ConfirmDialog is built on
  // `react-i18next`, which this app does not wire up. Revoking is reversible in
  // one grant, so the bar is "not a misclick", not "are you certain".
  const [confirming, setConfirming] = useState("");

  const refresh = () => qc.invalidateQueries({ queryKey: permissionKeys.grants });

  const grant = useMutation({
    mutationFn: setBiGrant,
    onSuccess: (_ack, sent) => {
      toast.add({ title: `${sent.email} now holds ${sent.access} access.` });
      setEmail("");
      void refresh();
    },
    onError: (e) => toast.add({ title: errorMessage(e), data: { tone: "danger" } }),
  });

  const revoke = useMutation({
    mutationFn: revokeBiGrant,
    onSuccess: (_ack, addr) => {
      // The server caches a resolved grant for 30s per instance, so a revoked
      // colleague can still load a page for up to that long. Saying so is the
      // difference between a reader trusting this table and re-clicking Revoke.
      toast.add({ title: `${addr} revoked. Takes effect within 30 seconds.` });
      setConfirming("");
      void refresh();
    },
    onError: (e) => toast.add({ title: errorMessage(e), data: { tone: "danger" } }),
  });

  if (grants.isPending) return <LoadingState label="Loading the access list…" />;

  // A 403 here is not a fault, it is the answer: this page needs `full` and the
  // reader holds `read`. An ErrorState with a Retry button would invite them to
  // click it forever, so this branch names what they hold and what fixes it.
  if (grants.isError && grants.error instanceof ApiError && grants.error.isForbidden) {
    return (
      <div className="bi-page">
        <div className="bi-page__head">
          <h1 className="bi-page__title">Permissions</h1>
        </div>
        <div className="bi-panel">
          <div className="bi-empty">
            <p style={{ margin: 0, fontWeight: 500 }}>You hold read access.</p>
            <p style={{ maxWidth: "60ch", margin: "0 auto" }}>
              Managing the access list needs full access, and the list of who can reach the
              warehouse is itself sensitive, so read holders cannot see it either. Ask a colleague
              who holds full access to grant you, or to make the change for you.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (grants.isError) {
    return <ErrorState error={grants.error} onRetry={() => void grants.refetch()} />;
  }

  const mine = me.data?.email ?? "";
  const rows = sortGrants(grants.data.grants);
  const nFull = rows.filter((r) => r.access === "full").length;
  const busy = grant.isPending || revoke.isPending;

  return (
    <div className="bi-page">
      <div className="bi-page__head">
        <h1 className="bi-page__title">Permissions</h1>
        <p className="bi-page__sub">
          Who may reach BI, and at what level. A grant here is independent of recruiter's admin
          console: the two lists have been separate since BI moved, so granting someone there does
          not grant them here.
        </p>
      </div>

      <div className="bi-panel">
        <div className="bi-stats">
          <div>
            <div className="bi-stat__value">{rows.length}</div>
            <div className="bi-stat__label">holders</div>
          </div>
          <div>
            <div className="bi-stat__value">{nFull}</div>
            <div className="bi-stat__label">can spend</div>
          </div>
        </div>
      </div>

      {/* Add or promote. One form for both, because the row is keyed on the
          address and the server upserts: typing an existing holder's address
          changes their level rather than failing as a duplicate. */}
      <div className="bi-panel" style={{ marginTop: "var(--rc-space-4)" }}>
        <form
          className="bi-grant-form"
          onSubmit={(e) => {
            e.preventDefault();
            const addr = email.trim();
            if (addr) grant.mutate({ email: addr, access });
          }}
        >
          <Input
            type="email"
            value={email}
            placeholder="colleague@fiverr.com"
            aria-label="Email to grant"
            autoComplete="off"
            disabled={busy}
            onChange={(e) => setEmail(e.target.value)}
          />
          <select
            className="bi-select"
            value={access}
            aria-label="Access level"
            disabled={busy}
            onChange={(e) => setAccess(e.target.value === "full" ? "full" : "read")}
          >
            <option value="read">Read only</option>
            <option value="full">Full access</option>
          </select>
          <Button type="submit" size="sm" disabled={busy || email.trim() === ""}>
            {grant.isPending ? "Granting…" : "Grant"}
          </Button>
        </form>
        <p className="bi-grant-form__blurb">{LEVEL_BLURB[access]}</p>
        <p className="bi-grant-form__blurb">
          The address must sit in a Workspace domain BI admits. A grant to an outside address is
          accepted here and still refused at sign in, because the allowlist answers a different
          question: the domain says whether someone is a colleague, this list says whether a
          colleague may read BI.
        </p>
      </div>

      <div className="bi-panel" style={{ marginTop: "var(--rc-space-4)" }}>
        <div className="bi-table-wrap">
          <table className="bi-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Access</th>
                <th>Granted by</th>
                <th>Granted</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const self = isSelfGrant(r.email, mine);
                return (
                  <tr key={r.email}>
                    <td>
                      {r.email}
                      {self ? (
                        <>
                          {" "}
                          <Badge size="sm" tone="neutral" variant="soft">
                            you
                          </Badge>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <Badge
                        size="sm"
                        tone={r.access === "full" ? "warning" : "neutral"}
                        variant="soft"
                      >
                        {r.access}
                      </Badge>
                    </td>
                    {/* A seeded or migrated row carries no grantor, and that is a
                        class of row an audit isolates rather than a blank it
                        skips. */}
                    <td>{r.grantedBy || "Not recorded"}</td>
                    <td>{formatDay(r.grantedAt)}</td>
                    <td>
                      {self ? (
                        <span
                          className="bi-grant-row__self"
                          title="The server refuses an edit to your own row, so an escalation takes two people."
                        >
                          Your own access
                        </span>
                      ) : (
                        <div className="bi-grant-row__actions">
                          <select
                            className="bi-select"
                            value={r.access}
                            aria-label={`Access level for ${r.email}`}
                            disabled={busy}
                            onChange={(e) =>
                              grant.mutate({
                                email: r.email,
                                access: e.target.value === "full" ? "full" : "read",
                              })
                            }
                          >
                            <option value="read">Read only</option>
                            <option value="full">Full access</option>
                          </select>
                          <Button
                            variant={confirming === r.email ? "solid" : "ghost"}
                            tone="danger"
                            size="sm"
                            disabled={busy}
                            onClick={() => {
                              if (confirming === r.email) revoke.mutate(r.email);
                              else setConfirming(r.email);
                            }}
                            onBlur={() => setConfirming("")}
                          >
                            {confirming === r.email ? "Confirm revoke" : "Revoke"}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
